import React, { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router'
import { observer } from 'mobx-react-lite'

// evertel
import { APIDataRoomExtended } from '@evertel/stores'
import {
    Button, Text, Input, Label, Row, Col, ToggleSwitch, Icon,
    InfoBox, useUI
} from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { useInvite } from '@evertel/web/invites'
import { SessionService } from '@evertel/session/SessionService'
import { CurrentUserController } from '@evertel/blue-user'
import { DepartmentController } from '@evertel/department'
import { RoomSettingsController, SharedRoomController } from '@evertel/room'
import { RoomContext } from '../../RoomContext'
import { RoomSettingsOptions } from './RoomSettingsOptions'
import { CloneRoomModal, SharedRoomModal, ICRoomModal } from '../modals'
import { EnabledConnectorsList } from '../EnabledConnectorsList'


interface RoomSettingsProps {
    isOpen: boolean
}

const RoomSettings: React.FC<RoomSettingsProps> = observer(() => {

    const sessionService = useService(SessionService, [])
    const currentUserController = useService(CurrentUserController, [])
    const {
        roomController,
        toggleAside,
        asideOpenComplete,
        canManageRoom
    } = useContext(RoomContext)

    const room = roomController.room || {} as APIDataRoomExtended

    const roomSettingsController = useService(RoomSettingsController, [room.id])
    const shareRoomController = useService(SharedRoomController, [room.id])
    const departmentController = useService(DepartmentController, [])

    const { openInvite } = useInvite()
    const { addToast, isConfirmed } = useUI()
    const navigate = useNavigate()

    const [roomName, setRoomName] = useState('')
    const [description, setDescription] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [hasChanges, setHasChanges] = useState(false)
    const [showSharedRoomModal, setShowSharedRoomModal] = useState(false)
    const [showCloneRoomModal, setShowCloneRoomModal] = useState(false)
    const [showICRoomModal, setShowICRoomModal] = useState(false)

    useEffect(() => {
        (async () => {
            setIsLoading(true)
            setHasChanges(false)

            if (room.id) {
                await roomSettingsController.init(room.id)
                shareRoomController.init(room.id)
                departmentController.init(room.departmentId)

                setRoomName(roomSettingsController.room.name || '')
                setDescription(roomSettingsController.room.description || '')
            } else {
                setRoomName('')
                setDescription('')
            }
            setIsLoading(false)
        })()
    }, [room.id, roomSettingsController.room])

    const saveSettings = async () => {

        if (!roomName.trim()) {
            addToast({
                color: 'warning',
                message: 'Please provide your room a name'
            })
            return
        }

        setIsLoading(true)
        try {
            await roomSettingsController.update({
                name: roomName.trim(),
                description: description?.trim() || ''
            })

            setHasChanges(false)

        } catch (error) {
            addToast({
                color: 'danger',
                message: error.message
            })
        }

        setIsLoading(false)
    }

    // handles each setting toggle
    const onToggleOption = async (option, value) => {
        try {
            await roomSettingsController.update(
                ['isSearchable', 'isArchived'].includes(option)
                    ? {
                        [option]: value
                    }
                    : {
                        options: {
                            [option]: value
                        }
                    }
            )
        } catch (error) {
            addToast({
                color: 'danger',
                message: error?.message || error
            })
        }
    }

    // Check for changes
    useEffect(() => {
        if (roomController.room) {
            const hasNameChanged = roomName !== roomController.room.name
            const hasDescriptionChanged = description !== roomController.room.description
            setHasChanges(hasNameChanged || hasDescriptionChanged)
        }
    }, [roomName, description, roomController.room])

    const onOpenInvite = () => {
        openInvite('room', [{
            label: room.name,
            value: room.id
        }])
    }

    const leaveRoom = async () => {
        try {
            const confirmed = await isConfirmed({
                title: 'Leave Room?',
                message: 'Are you sure you want to remove yourself as a member of this room?',
                acceptButton: {
                    label: 'Yes, remove me',
                    color: 'danger'
                }
            })
            if (!confirmed) return

            await roomController.leaveRoom()

            addToast({
                color: 'success',
                message: `You have been removed from #${room.name}`
            })

            sessionService.deleteRoomFromNavigationHistory(room.id)

            navigate('/landing')

            toggleAside('settings', 'close')

        } catch (error: any) {
            addToast({
                color: 'danger',
                title: 'Error',
                message: error?.message || error
            })
        }
    }

    if (!room.id || !asideOpenComplete) return null

    const options = room.options as any
    const canLeave = !options.autoAddDepartmentMembers && options?.roomMembersCanLeave
    const isGuest = currentUserController.isGuestInDepartment(room.departmentId)
    const hasManagementOrExecutiveRole = currentUserController.canManageInDepartment(room.departmentId)
    const activeLinkAvailable = !!shareRoomController.latestActiveInvite
    const canShowRegionalCollaboration = (activeLinkAvailable || (!activeLinkAvailable && !isGuest))
    const isExecutive = currentUserController.canExecutiveInDepartment(room.departmentId)

    return (
        <>
            <Row className="mb-4 position-relative">
                <Col className="flex-grow-1">
                    {(canManageRoom) &&
                        <>
                            <Label text="Room Name" className="mb-2" bold>
                                <Input
                                    type="text"
                                    name="name"
                                    value={roomName}
                                    disabled={!canManageRoom || room.isArchived || isGuest}
                                    onChange={(e) => setRoomName(e.target.value)}
                                    placeholder="Ex: Squad Name"
                                />
                            </Label>
                            <Label text="Description" className="mb-3" bold>
                                <Input
                                    type="text"
                                    name="description"
                                    value={description}
                                    disabled={!canManageRoom || room.isArchived || isGuest}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Enter a room description"
                                />
                            </Label>

                            {(hasChanges) && (
                                <Row align="right">
                                    <Button
                                        size="sm"
                                        onClick={saveSettings}
                                        color="primary"
                                        className="fade-in"
                                        loading={isLoading}
                                        disabled={isLoading}
                                    >
                                        Save Name & Description
                                    </Button>
                                </Row>
                            )}

                        </>
                    }
                    {(!canManageRoom) && (
                        <>
                            <Text
                                bold
                                className="mt-2">
                                Room Name
                            </Text>
                            <div className="p-2 m-2 border rounded">
                                {roomName}
                            </div>
                            {!!description && (
                                <>
                                    <Text
                                        bold
                                        className="mt-2"
                                    >
                                        Description
                                    </Text>
                                    <div className="p-2 m-2 border rounded"
                                    >
                                        {description}
                                    </div>
                                </>
                            )}
                        </>
                    )}

                </Col>
            </Row>

            {!isExecutive && <EnabledConnectorsList />}

            {(canManageRoom) &&
                <>
                    {(!isGuest) &&
                        <RoomSettingsOptions
                            room={room}
                            isSearchable={room.isSearchable}
                            isArchived={room.isArchived}
                            onChangeOption={onToggleOption}
                            options={options}
                            onOpenICModal={() => setShowICRoomModal(true)}
                        />}

                    {isExecutive && departmentController.integrationsEnabled && (
                        <Row className="mt-2 mb-2 pe-2">
                            <Button
                                color="text"
                                className="w-100 d-flex justify-content-start ps-0 align-items-center"
                                onClick={() => toggleAside('connectors', 'open')}
                            >
                                <Icon
                                    name="share-nodes"
                                    className="me-3"
                                    fixedWidth={true}
                                />
                                <strong className="flex-grow-1 text-start">Manage Connectors</strong>
                                <Icon
                                    name="chevron-right"
                                    size={13}
                                />
                            </Button>
                        </Row>
                    )}

                    {(canShowRegionalCollaboration) &&
                        <InfoBox
                            color="primary"
                            variant="outline"
                            className="my-5"
                        >
                            <Text
                                tag="div"
                                bold
                            >
                                Regional Collaboration
                            </Text>
                            <Text
                                tag="div"
                                size="small"
                            >
                                Work with other agencies in this room.
                            </Text>
                            <Row className="mt-2">
                                {(!isGuest) &&
                                    <Button
                                        color="primary"
                                        outline
                                        size="sm"
                                        onClick={onOpenInvite}
                                        disabled={room.isArchived}
                                        className="me-2">
                                        <Icon
                                            name="user-plus"
                                            className="me-1"
                                        />
                                        Invite Guests
                                    </Button>
                                }
                                <Button
                                    color="primary"
                                    outline
                                    size="sm"
                                    onClick={() => setShowSharedRoomModal(true)}
                                    disabled={room.isArchived}
                                    className="me-2">
                                    <Icon
                                        name="link-horizontal"
                                        className="me-1"
                                    />
                                    Share Link
                                </Button>
                            </Row>
                        </InfoBox>
                    }
                </>
            }

            <Row className="pb-2 border-bottom mt-5 border-top pt-2">
                <div className="col-1">
                    <Icon
                        name={(roomSettingsController.userRoomNotificationsEnabled) ? 'bell' : 'bell-slash'}
                        type={(roomSettingsController.userRoomNotificationsEnabled) ? 'solid' : undefined}
                        color={(roomSettingsController.userRoomNotificationsEnabled) ? 'success' : 'muted'}
                        className="me-2"
                        fixedWidth
                    />
                </div>
                <Col
                    xs={9}
                    className="ps-0"
                >
                    <Text bold>
                        My Room Notifications
                    </Text>
                    <Text
                        size="smaller">
                        Enable/disable new message notifications in this room (effects you only).
                    </Text>
                </Col>
                <Col align="right">
                    <ToggleSwitch
                        name="roomNotifications"
                        id="roomNotifications"
                        onChange={(checked) => roomSettingsController.updateUserRoomNotificationSettings(checked)}
                        checked={roomSettingsController.userRoomNotificationsEnabled}
                        optionLabels={['On', 'Off']}
                    />
                </Col>
            </Row>
            <div className="mt-4">
                <Row className="mb-2">
                    {hasManagementOrExecutiveRole && (
                        <Col xs={6}>
                            <Button
                                color="link"
                                className="w-100 text-left"
                                onClick={() => setShowCloneRoomModal(true)}
                            >
                                <Icon
                                    name="clone"
                                    className="me-3"
                                    fixedWidth={true}
                                />
                                Clone Room
                            </Button>
                        </Col>
                    )}
                    {canManageRoom && (
                        <Col xs={6}>
                            <Button
                                color="link"
                                className="w-100 text-left"
                                onClick={() => toggleAside('media-search', 'open', { initialTab: 'room_exports'})}
                            >
                                <Icon
                                    name="plus-circle"
                                    className="me-3"
                                    fixedWidth={true}
                                />
                                Export Room
                            </Button>
                        </Col>
                    )}
                </Row>

                {canLeave && (
                    <Row className="mt-4">
                        <Col xs={6}>
                            <Button
                                color="link"
                                className="w-100 text-left"
                                onClick={leaveRoom}
                            >
                                <Icon
                                    name="xmark"
                                    color="danger"
                                    className="me-3"
                                    fixedWidth={true}
                                />
                                <Text color="danger">
                                    Leave Room
                                </Text>
                            </Button>

                        </Col>
                    </Row>
                )}
            </div>

            <Text
                size="smaller"
                italic
                style={{
                    float: 'right'
                }}
            >
                Room ID: {room.id}
            </Text>

            <SharedRoomModal
                visible={showSharedRoomModal}
                onClose={() => setShowSharedRoomModal(false)}
                room={room}
                userIsGuest={isGuest}
            />
            <CloneRoomModal
                visible={showCloneRoomModal}
                onClose={() => setShowCloneRoomModal(false)}
            />
            <ICRoomModal
                visible={showICRoomModal}
                onClose={() => setShowICRoomModal(false)}
            />
        </>
    )
})

export { RoomSettings }
